import React, { useState } from 'react';
import { Box, Button, Typography, Stack, LinearProgress, Alert } from '@mui/material';
import { ref, uploadBytesResumable, getDownloadURL, getStorage } from 'firebase/storage';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CancelIcon from '@mui/icons-material/Cancel';

interface FileUploadProgress {
  file: File;
  progress: number;
  url?: string;
  error?: string;
}

interface FileUploadProps {
  onFileUpload: (urls: string[]) => void;
  onFileRemove: (url: string) => void;
  restaurantId: string;
  uoId?: string;
  guestName?: string;
  existingUrls?: string[];
  disabled?: boolean;
  /**
   * Optional maximum number of files allowed. Defaults to 4.
   */
  maxFiles?: number;
  /**
   * Optional maximum file size in MB. Defaults to 5.
   */
  maxFileSizeMB?: number;
}

export default function FileUpload({
  onFileUpload,
  onFileRemove,
  restaurantId,
  uoId,
  guestName,
  existingUrls,
  disabled,
  maxFiles = 4,
  maxFileSizeMB = 5,
}: FileUploadProps) {
  const [uploadProgress, setUploadProgress] = useState<FileUploadProgress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const storage = getStorage();

  const isUploading = uploadProgress.some(p => p.progress < 100 && !p.error);
  const totalFilesCount =
    (existingUrls?.length || 0) + uploadProgress.length;
  const isAtMaxFiles = totalFilesCount >= maxFiles;
  const disableUploadControl = disabled || isUploading || isAtMaxFiles;

  const buildObjectPath = (file: File) => {
    const originalName = file.name || 'image';
    const dotIndex = originalName.lastIndexOf('.');
    const extension = dotIndex > -1 ? originalName.substring(dotIndex) : '';

    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthLabel = monthNames[now.getMonth()];
    const year = now.getFullYear();
    const dateFolder = `${day}-${monthLabel}-${year}`;

    const safeGuestName = (guestName || 'guest')
      .trim()
      .substring(0, 30)
      .replace(/[^a-zA-Z0-9-_]/g, '_');
    const safeUoId = (uoId || 'noid').replace(/[^a-zA-Z0-9-_]/g, '_');
    const timestamp = Date.now();
    const finalName = `${safeUoId}-${safeGuestName}-${timestamp}${extension}`;

    return `room-billing/${restaurantId}/${dateFolder}/${finalName}`;
  };

  const uploadFile = (file: File, options?: { reuseEntry?: boolean }) => {
    const storageRef = ref(storage, buildObjectPath(file));
    const uploadTask = uploadBytesResumable(storageRef, file);

    if (!options?.reuseEntry) {
      setUploadProgress(prev => [...prev, { file, progress: 0 }]);
    } else {
      // reset existing entry state for this file
      setUploadProgress(prev =>
        prev.map(p =>
          p.file === file
            ? {
                ...p,
                progress: 0,
                error: undefined,
                url: undefined,
              }
            : p,
        ),
      );
    }

    return new Promise<string>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        snapshot => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(prev =>
            prev.map(p => (p.file === file ? { ...p, progress } : p)),
          );
        },
        error => {
          console.error('Upload error:', error);
          setUploadProgress(prev =>
            prev.map(p =>
              p.file === file
                ? {
                    ...p,
                    error: 'Failed to upload file. Please try again.',
                  }
                : p,
            ),
          );
          reject(error);
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            setUploadProgress(prev =>
              prev.map(p =>
                p.file === file
                  ? {
                      ...p,
                      progress: 100,
                      url,
                    }
                  : p,
              ),
            );
            resolve(url);
          } catch (err) {
            console.error('Failed to get download URL:', err);
            reject(err);
          }
        },
      );
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    if (isAtMaxFiles) {
      setError(`You have already uploaded the maximum of ${maxFiles} images. Please remove an existing image before uploading a new one.`);
      e.target.value = '';
      return;
    }

    const allSelectedFiles = Array.from(e.target.files);
    const remainingSlots = maxFiles - totalFilesCount;

    if (remainingSlots <= 0) {
      setError(`You can upload a maximum of ${maxFiles} images.`);
      return;
    }

    const maxBytes = maxFileSizeMB * 1024 * 1024;
    const validFiles: File[] = [];
    const validationErrors: string[] = [];

    for (const file of allSelectedFiles) {
      if (!file.type.startsWith('image/')) {
        validationErrors.push(`${file.name}: Only image files are allowed.`);
        continue;
      }

      if (file.size > maxBytes) {
        validationErrors.push(
          `${file.name}: File is larger than ${maxFileSizeMB}MB.`,
        );
        continue;
      }

      if (validFiles.length >= remainingSlots) break;
      validFiles.push(file);
    }

    if (validationErrors.length) {
      setError(validationErrors.join(' '));
    } else {
      setError(null);
    }

    if (!validFiles.length) {
      return;
    }

    try {
      const urls = await Promise.all(validFiles.map((file) => uploadFile(file)));
      onFileUpload(urls);
    } catch (err) {
      setError('Failed to upload one or more images. Please try again.');
      console.error('Upload error:', err);
    } finally {
      // reset the input so the same file can be selected again if needed
      e.target.value = '';
    }
  };

  const handleRemoveFile = (url: string) => {
    onFileRemove(url);
    setUploadProgress(prev => prev.filter(p => p.url !== url));
  };

  const handleRetryUpload = (file: File) => {
    setError(null);

    uploadFile(file, { reuseEntry: true })
      .then((url) => {
        onFileUpload([url]);
      })
      .catch((err) => {
        console.error('Retry upload error:', err);
        setError('Failed to upload image. Please try again.');
      });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" gutterBottom>
            Upload Guest ID Images
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Maximum {maxFiles} images (currently {totalFilesCount}), Maximum size {maxFileSizeMB}MB each.
          </Typography>
        </Box>
        <Button
          variant="contained"
          component="label"
          startIcon={<FileUploadIcon />}
          disabled={disableUploadControl}
          sx={{
            width: { xs: '100%', sm: 'auto' },
            mt: { xs: 1, sm: 0 },
          }}
        >
          {isUploading
            ? 'Uploading…'
            : isAtMaxFiles
            ? 'Maximum images reached'
            : 'Upload Images'}
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            disabled={disableUploadControl}
            onChange={handleFileUpload}
          />
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Existing images (already uploaded) */}
      {!!existingUrls?.length && (
        <Stack
          spacing={2}
          direction="row"
          flexWrap="wrap"
          sx={{ mt: 1 }}
        >
          {existingUrls.map((url) => {
            const nameFromUrl = decodeURIComponent(url.split('/').pop() || '').split('?')[0];

            return (
              <Box
                key={url}
                sx={{
                  width: { xs: '100%', sm: 140 },
                  borderRadius: 2,
                  bgcolor: '#fff',
                  boxShadow: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {/* <Box sx={{ p: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mb: 1 }}
                    noWrap
                    title={nameFromUrl}
                  >
                    {nameFromUrl}
                  </Typography>
                </Box> */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    pt: '75%', // 4:3 ratio
                    overflow: 'hidden',
                    bgcolor: 'grey.100',
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    alt={nameFromUrl}
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                </Box>
                <Button
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => handleRemoveFile(url)}
                  sx={{ borderRadius: 0 }}
                >
                  Remove
                </Button>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* New uploads in this session */}
      <Stack
        spacing={2}
        direction="row"
        flexWrap="wrap"
        sx={{ mt: existingUrls?.length ? 2 : 1 }}
      >
        {uploadProgress.map((progress, index) => {
          const previewUrl = URL.createObjectURL(progress.file);

          return (
            <Box
              key={`${progress.file.name}-${index}`}
              sx={{
                width: { xs: '100%', sm: 140 },
                borderRadius: 2,
                bgcolor: '#fff',
                boxShadow: 1,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 1 }}
                  noWrap
                  title={progress.file.name}
                >
                  {progress.file.name}
                </Typography>
                {progress.error ? (
                  <>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {progress.error}
                    </Alert>
                    <Button
                      size="small"
                      onClick={() => handleRetryUpload(progress.file)}
                      sx={{ mt: 1 }}
                    >
                      Re-upload
                    </Button>
                  </>
                ) : (
                  <LinearProgress
                    variant="determinate"
                    value={progress.progress}
                    sx={{ height: 6, borderRadius: 999 }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  pt: '75%', // 4:3 ratio
                  overflow: 'hidden',
                  bgcolor: 'grey.100',
                }}
              >
                <Box
                  component="img"
                  src={previewUrl}
                  alt={progress.file.name}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              {progress.url && (
                <Button
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => handleRemoveFile(progress.url!)}
                  sx={{ borderRadius: 0 }}
                >
                  Remove
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
